// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useState } from "react";
import { Check } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import AppDialog from "@/components/common/AppDialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { FormCalendar, FormInput, FormTextarea } from "@/components/forms/FormControls";
import { scheduleTaskSchema } from "@/schemas/deals";

/**
 * ScheduleTaskModal — opens when the agent marks a "scheduled"-titled task complete,
 * capturing the real-world date/time and any relevant details (attendees, contact,
 * meeting location). Save writes both status='complete' and the scheduled_at / notes.
 */
export default function ScheduleTaskModal({ task, onSaved, onSkip, onCancel }) {
  const now = new Date();
  const defaultDate = now.toISOString().slice(0, 10);
  const defaultTime = now.toTimeString().slice(0, 5);
  const [saving, setSaving] = useState(false);
  const form = useForm({
    resolver: zodResolver(scheduleTaskSchema),
    defaultValues: {
      date: task?.scheduled_at?.slice(0, 10) || defaultDate,
      time: task?.scheduled_at ? new Date(task.scheduled_at).toTimeString().slice(0, 5) : defaultTime,
      notes: task?.scheduled_notes || "",
    },
  });

  const submit = async ({ date, time, notes }) => {
    setSaving(true);
    const iso = new Date(`${date}T${time || "00:00"}`).toISOString();
    try {
      await onSaved({ scheduled_at: iso, scheduled_notes: notes });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open={Boolean(task)}
      onOpenChange={(open) => { if (!open) onCancel(); }}
      eyebrow="Log the details"
      title={task?.title || "Schedule task"}
      description="This will pin the event to the top of the deal so everyone can see it."
      testId="schedule-task-modal"
    >
      <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5" noValidate>
          <div className="grid grid-cols-2 gap-4">
            <FormCalendar name="date" label="Date" data-testid="schedule-date" required />
            <FormInput name="time" label="Time" type="time" data-testid="schedule-time" />
          </div>
          <FormTextarea name="notes" label="Details (optional)" rows={3} placeholder="e.g. Ace Home Inspections · buyer + agent attending · access via lockbox" data-testid="schedule-notes" />

        <div className="flex items-center justify-between gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onSkip}
            data-testid="schedule-skip"
          >
            Complete without details
          </Button>
          <Button
            type="submit"
            variant="dashboardPrimary"
            loading={saving}
            data-testid="schedule-save"
          >
            <Check data-icon="inline-start" /> Save & complete
          </Button>
        </div>
      </form>
      </Form>
    </AppDialog>
  );
}
